import type { ApiError } from '@chikara/types';
import { APP_VERSION, isQaFrontendHost } from '@/config/version';
import { track } from './openpanel';

export type ErrorCategory = 'api' | 'network' | 'auth' | 'validation' | 'ui' | 'unknown';
export type ErrorSource = 'apiClient' | 'errorBoundary' | 'trycatch' | 'service';

export interface TrackErrorInput {
  source: ErrorSource;
  category: ErrorCategory;
  error: unknown;
  context?: Record<string, unknown>;
}

const lastSeen = new Map<string, number>();

export function sanitizeText(input: unknown, maxLen = 2000): string {
  const text = String(input ?? '');
  let out = text;

  // emails
  out = out.replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[REDACTED]');
  // jwt (rough)
  out = out.replace(
    /eyJ[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}\.[a-zA-Z0-9_-]{10,}/g,
    '[REDACTED]'
  );
  // bearer tokens
  out = out.replace(/Bearer\s+[A-Za-z0-9._-]+/gi, 'Bearer [REDACTED]');
  // uuid
  out = out.replace(
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
    '[REDACTED]'
  );
  // common secrets in query / key-value
  out = out.replace(/\b(token|password|code|secret|authorization)=([^&\s]+)/gi, '$1=[REDACTED]');

  if (out.length > maxLen) return out.slice(0, maxLen);
  return out;
}

/** Quita query params sensibles y deja solo path para analítica. */
export function sanitizeRequestUrl(url: string): { url: string; path: string } {
  try {
    const parsed = new URL(url);
    const sensitiveParams = ['token', 'password', 'code', 'secret', 'authorization'];
    sensitiveParams.forEach((key) => parsed.searchParams.delete(key));
    const sanitizedUrl = sanitizeText(parsed.toString(), 500);
    const sanitizedPath = sanitizeText(`${parsed.pathname}${parsed.search}`, 300);
    return { url: sanitizedUrl, path: sanitizedPath };
  } catch {
    return { url: sanitizeText(url, 500), path: sanitizeText(url, 300) };
  }
}

export function createFingerprint(args: {
  category: ErrorCategory;
  code?: string;
  path?: string;
  topStack?: string;
  endpoint?: string;
}): string {
  return [
    args.category,
    args.code ?? '',
    args.endpoint ?? '',
    args.path ?? '',
    args.topStack ?? '',
  ].join('|');
}

export function shouldDedupe(fingerprint: string, windowMs: number): boolean {
  const now = Date.now();
  const prev = lastSeen.get(fingerprint);
  if (prev && now - prev < windowMs) return true;
  lastSeen.set(fingerprint, now);
  return false;
}

function extractErrorBasics(error: unknown): { message?: string; stack?: string; name?: string } {
  if (error instanceof Error) {
    return { message: error.message, stack: error.stack, name: error.name };
  }
  if (typeof error === 'object' && error && 'message' in error) {
    return { message: String((error as any).message) };
  }
  return { message: typeof error === 'string' ? error : undefined };
}

function safeString(value: unknown): string | undefined {
  if (value == null) return undefined;
  const s = String(value);
  return s.length ? s : undefined;
}

function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'type' in error &&
    ((error as ApiError).type === 'http' || (error as ApiError).type === 'network')
  );
}

/** Log detallado en consola solo en https://chikara-frontend.qa-albo.tech/ */
export function logQaFrontendError(payload: Record<string, unknown>): void {
  if (!isQaFrontendHost()) return;
  console.error(`[chikara-frontend v${APP_VERSION}] error`, payload);
}

export function extractApiErrorTelemetry(error: ApiError): Record<string, unknown> {
  const backend = error.backend;
  const backendMessage = error.backendMessage ?? backend?.error?.message;

  return {
    apiErrorType: error.type,
    userMessage: sanitizeText(error.message, 300),
    backendMessage: sanitizeText(backendMessage, 500),
    backendCode: safeString(error.code ?? backend?.error?.code),
    traceId: safeString(error.traceId ?? backend?.error?.trace_id),
    requestId: safeString(backend?.meta?.request_id),
    backendTimestamp: safeString(backend?.meta?.timestamp ?? error.timestamp),
    validationDetails: error.details
      ? sanitizeText(JSON.stringify(error.details), 1000)
      : undefined,
  };
}

export function trackError({ source, category, error, context }: TrackErrorInput): void {
  const basics = extractErrorBasics(error);
  const path = typeof window !== 'undefined' ? window.location.pathname : undefined;
  const search = typeof window !== 'undefined' ? window.location.search : undefined;

  const topStack = sanitizeText(basics.stack ?? '').split('\n')[0] ?? '';
  const code =
    typeof error === 'object' && error && 'code' in error ? safeString((error as any).code) : undefined;
  const status =
    typeof error === 'object' && error && 'status' in error
      ? Number((error as any).status)
      : undefined;

  const requestEndpoint =
    typeof context?.request === 'object' &&
    context.request &&
    'endpoint' in (context.request as object)
      ? safeString((context.request as { endpoint?: string }).endpoint)
      : undefined;

  const apiTelemetry = isApiError(error) ? extractApiErrorTelemetry(error) : undefined;

  const fingerprint = createFingerprint({
    category,
    code: code ?? apiTelemetry?.backendCode as string | undefined,
    path,
    topStack,
    endpoint: requestEndpoint,
  });
  if (shouldDedupe(fingerprint, 10_000)) return;

  const eventPayload: Record<string, unknown> = {
    category,
    source,
    code,
    status,
    name: safeString(basics.name),
    message: sanitizeText(basics.message, 300),
    stack: sanitizeText(basics.stack, 4000),
    route: {
      path,
      search: sanitizeText(search, 500),
    },
    fingerprint,
    appVersion: APP_VERSION,
    ...apiTelemetry,
    ...context,
  };

  track('error', eventPayload);
  logQaFrontendError(eventPayload);
}
